import { useState } from "react";
import { accountService } from "@miplatform/api";
import {
  HTTP_STATUSES,
  INVOICE_STATUSES,
  MAX_PAGE_SIZE,
} from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  ICreateMemoRequest,
  ICreatePaymentRequest,
  ICreditMemoDetailsResponse,
  IInvoiceResponse,
  QuickBookSyncStatus,
} from "@miplatform/types";
import { buildSearchQuery } from "@miplatform/utils";
import { useGlobalManagerApproval } from "@mibuildweb/components";

export type optionsType = { label: string; value: string };

export const usePostPaymentsViewModel = ({
  paymentId,
}: {
  paymentId: string;
}) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<IInvoiceResponse[]>([]);
  const [bankAccountsOptions, setBankAccountsOptions] = useState<optionsType[]>(
    []
  );

  const [creditMemos, setCreditMemos] = useState<ICreditMemoDetailsResponse[]>(
    []
  );
  const [search, setSearch] = useState<string>("");

  const debouncedSearch = useDebounce(search, 500);
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const { executeApiRequest } = useGlobalManagerApproval();

  const searchQuery = buildSearchQuery(debouncedSearch, [
    "home.address.addressLine1",
    "orderNumber",
  ]);

  const buildInvoiceFilters = (customerAccountId: string) => {
    const customerFilter = {
      billToAccountId: { eq: { type: "guid", value: customerAccountId } },
    };

    const syncedInvoiceFilter = {
      ...customerFilter,
      quickBooksAuditResponse: { status: { eq: QuickBookSyncStatus.Synced } },
      status: { eq: INVOICE_STATUSES.INVOICED },
    };

    const existingPaymentFilter = {
      invoicePayments: {
        any: { paymentId: { eq: { type: "guid", value: paymentId } } },
      },
    };

    const isEditing = Boolean(paymentId);
    const hasSearch = Object.keys(searchQuery).length > 0;

    if (isEditing) {
      const searchableInvoicesFilter = hasSearch
        ? { and: [syncedInvoiceFilter, searchQuery] }
        : syncedInvoiceFilter;

      return { or: [existingPaymentFilter, searchableInvoicesFilter] };
    }

    return hasSearch
      ? { and: [syncedInvoiceFilter, searchQuery] }
      : syncedInvoiceFilter;
  };

  const getCustomers = () => {
    return stateManager.run(async () => {
      const response = await accountService.accounts.getAll({
        sort: "name",
        // filters: { removed till further notice
        //   accountType: {
        //     in: [AccountType.Builder, AccountType.Service],
        //   },
        // },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setCustomers(
          response.data.map(
            (customer: {
              name: string;
              id: string;
              defaultRetainagePercentage: number;
              defaultDiscountPercentage: number;
            }) => {
              return {
                label: customer.name,
                id: customer.id,
                defaultRetainagePercentage:
                  customer.defaultRetainagePercentage || 0,
                defaultDiscountPercentage:
                  customer.defaultDiscountPercentage || 0,
              };
            }
          ) || []
        );
      }
      return [];
    });
  };

  const getInvoices = ({
    customerAccountId,
  }: {
    customerAccountId: string;
  }) => {
    return stateManager.run(async () => {
      const response = await accountService.invoices.getAll({
        top: MAX_PAGE_SIZE,
        filters: buildInvoiceFilters(customerAccountId),
        sort: "createdOn",
        enableCaching: false,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setInvoices(response.data);
      }
    });
  };

  const postPayment = (data: ICreatePaymentRequest, onSuccess?: () => void) => {
    return stateManager.run(async () => {
      const options = await accountService.payments.getCreateOptions({
        data,
      });

      const callback = (res: any) => {
        if (res?.status === HTTP_STATUSES.OK) {
          toastService.success({ text: "Payment posted successfully." });
          onSuccess?.();
        }
      };

      const response = await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Posting a payment requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });
      return response?.status === HTTP_STATUSES.OK ? response : null;
    });
  };

  const updatePayment = (
    id: string,
    data: ICreatePaymentRequest,
    modifiedOn: string,
    onSuccess?: () => void
  ) => {
    return stateManager.run(async () => {
      const options = await accountService.payments.getUpdateOptions({
        id,
        data,
        modifiedOn,
      });
      const callback = (res: any) => {
        if (res?.status === HTTP_STATUSES.OK) {
          toastService.success({ text: "Payment updated successfully." });
          onSuccess?.();
        }
      };
      const response = await executeApiRequest({
        options,
        callback,

        toastMessageForManagerApproval: {
          text: `Updating a payment requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });

      return response?.status === HTTP_STATUSES.OK ? response : null;
    });
  };

  const getPaymentDetails = (paymentId: string) => {
    return stateManager.run(async () => {
      const response = await accountService.payments.getById({ id: paymentId });
      return response.status === HTTP_STATUSES.OK ? response.data : null;
    });
  };

  const getBankAccounts = () => {
    return stateManager.run(async () => {
      const response = await accountService.bankAccounts.getAll();

      if (response.status === HTTP_STATUSES.OK) {
        setBankAccountsOptions(
          response.data.map(
            (bankAccounts: {
              alias: string;
              name: string;
              id: string;
              acctNum: string;
            }) => {
              return {
                label: bankAccounts.alias || bankAccounts.name,
                value: bankAccounts.id,
              };
            }
          ) || []
        );
      }
      return [];
    });
  };

  const getCreditMemos = async (customerAccountId: string) => {
    return stateManager.run(async () => {
      const baseFilters = {
        accountId: {
          eq: { type: "guid", value: customerAccountId },
        },
        voidDate: {
          eq: null,
        },
      };

      const filters = !paymentId
        ? {
            ...baseFilters,
            openAmount: {
              gt: 0,
            },
          }
        : {
            or: [
              {
                creditMemoPayments: {
                  any: {
                    paymentId: { eq: { type: "guid", value: paymentId } },
                  },
                },
              },
              {
                ...baseFilters,
                openAmount: {
                  gt: 0,
                },
              },
            ],
          };

      const response = await accountService.creditMemos.getAll({
        filters,
        sort: "home/address/addressLine1 asc"
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCreditMemos(response.data);
      }
      return [];
    });
  };

  const createCreditMemo = async (data: ICreateMemoRequest) => {
    return stateManager.run(async () => {
      const response = await accountService.creditMemos.create({
        data,
        toastMessageConfig: {
          success: {
            text: "Backcharge created successfully.",
          },
        },
      });

      return response;
    });
  };

  const createRetainage = async ({
    invoiceId,
    data,
  }: {
    invoiceId: string;
    data: { retainageAmount: number; modifiedOn: string };
    onSuccess?: () => void;
  }) => {
    return stateManager.run(async () => {
      const response = await accountService.invoices.splitInvoiceForRetainage({
        id: invoiceId,
        data,
      });
      if (response.status === HTTP_STATUSES.CREATED) {
        setInvoices((prevInvoices) => {
          const { parentRetainageResponse, childRetainageResponse } =
            response.data;

          return [
            ...prevInvoices
              .map((invoice) =>
                invoice.id === parentRetainageResponse.id
                  ? parentRetainageResponse
                  : invoice
              )
              .filter(
                (invoice) => invoice.status === INVOICE_STATUSES.INVOICED
              ),
            childRetainageResponse,
          ];
        });
      }
      return response;
    });
  };

  const voidCreditMemo = async (id: string, modifiedOn: string) => {
    return stateManager.run(async () => {
      const response = await accountService.creditMemos.void({
        id,
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Backcharge voided successfully.",
          },
        },
      });
      return response;
    });
  };

  return {
    isLoading,
    stateManager,
    search,
    getCustomers,
    customers,
    debouncedSearch,
    setSearch,
    setInvoices,
    getInvoices,
    invoices,
    postPayment,
    updatePayment,
    bankAccountsOptions,
    getBankAccounts,
    getPaymentDetails,
    getCreditMemos,
    creditMemos,
    createCreditMemo,
    voidCreditMemo,
    createRetainage,
    setCreditMemos,
  };
};
